/**
 * English (default) — SuperAdmin backoffice
 */
export default {
  nav: {
    dashboard: 'Dashboard',
    organizations: 'Organizations',
    users: 'Users',
    timeEntries: 'Time entries',
    audit: 'Audit',
    security: 'Security',
    changePassword: 'Change password',
    backofficeSubtitle: 'Backoffice — SuperAdmin',
    openMenu: 'Open menu',
    backups: 'Backups',
    notifDiagnostics: 'Notification diagnostics',
    systemStatus: 'System Status'
  },
  navSections: {
    general: 'General',
    management: 'Management',
    security: 'Security'
  },
  login: {
    title: 'ChatIN Clock',
    subtitle: 'Backoffice — SuperAdmin access',
    email: 'Email',
    password: 'Password',
    emailPlaceholder: 'you@email.com',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    show: 'Show',
    hide: 'Hide',
    logoAlt: 'ChatIN Clock logo',
    submit: 'Sign in',
    submitting: 'Signing in…',
    secureAccess: 'Secure admin access',
    footer: 'All actions are logged and monitored.',
    errorCredentials: 'Invalid credentials',
    errorRateLimit: 'Too many attempts. Please wait and try again.'
  },
  password: {
    changeTitle: 'Change password',
    changeDescription: 'Update your account password.',
    current: 'Current password',
    currentPlaceholder: 'Enter current password',
    new: 'New password',
    newPlaceholder: 'Enter new password',
    confirm: 'Confirm password',
    confirmPlaceholder: 'Confirm new password',
    changeButton: 'Change password',
    changing: 'Changing…',
    success: 'Password updated successfully',
    error: 'Failed to update password',
    requirements: 'Password requirements',
    minLength: 'Minimum {min} characters',
    complexity: 'At least 3 of: upper, lower, numbers, symbols',
    noCommon: 'Avoid common passwords',
    noSequences: 'Avoid obvious sequences (123, abc, qwerty)',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    tipsTitle: 'Security tips',
    tipStrong: 'Use a strong password',
    tipReuse: 'Avoid reusing passwords',
    tipManager: 'Use a password manager'
  },
  common: {
    loading: 'Loading…',
    save: 'Save',
    cancel: 'Cancel',
    clear: 'Clear',
    reset: 'Reset',
    all: 'All',
    search: 'Search',
    next: 'Next',
    previous: 'Previous',
    logout: 'Log out',
    of: 'of',
    page: 'Page',
    results: 'results',
    noResults: 'No results',
    error: 'Error',
    showIds: 'Show IDs',
    copied: 'Copied',
    copy: 'Copy'
  },
  actions: {
    actions: 'Actions',
    viewDetails: 'View details',
    view: 'View',
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    confirm: 'Confirm'
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Platform overview',
    loading: 'Loading dashboard…',
    activeOrgs: 'Active organizations',
    activeOrgsOf: 'of {total} total',
    activeUsers: 'Active users',
    entriesToday: 'Entries today',
    quickActions: 'Quick actions',
    quickActionsDesc: 'Shortcuts to the most common admin tasks.',
    createOrg: 'Create organization',
    createUser: 'Create user',
    viewOrgs: 'View organizations',
    viewUsers: 'View users',
    viewEntries: 'View time entries',
    openAudit: 'Open audit log',
    securitySettings: 'Security settings',
    changePassword: 'Reset my password',
    recentActivity: 'Recent activity',
    viewAll: 'View all',
    noRecentActivity: 'No recent activity yet.',
    platformShortcuts: 'Platform shortcuts',
    manageOrgs: 'Manage organizations',
    manageUsers: 'Manage users',
    reviewAudit: 'Review audit log',
    systemStatus: 'System status',
    apiStatus: 'API status',
    lastDeployment: 'Last deployment',
    twoFaAvailable: '2FA availability',
    statusOk: 'OK'
  },
  orgCards: {
    access: 'Access: {plan}{until}',
    accessUntil: ' until {until}',
    accessTitle: 'Access',
    until: 'until {date}',
    plan: 'Plan: {plan} • {source}',
    planTitle: 'Plan',
    planLabel: 'Plan',
    planSource: 'Plan source',
    status: 'Status',
    trial: 'Trial ends: {date}',
    trialLabel: 'Trial ends',
    grace: 'Grace ends: {date}',
    graceLabel: 'Grace ends',
    override: 'Override: {plan} until {date}',
    overrideLabel: 'Override',
    overrideNone: 'Override: —',
    none: '—',
    historyTitle: 'Trial / Grace / Override',
    colDate: 'Date',
    colAction: 'Action',
    colReason: 'Reason',
    colMeta: 'Metadata',
    noAudit: 'No audit records.'
  },
  orgActions: {
    grant7: '+7 days',
    grant14: '+14 days',
    grant30: '+30 days',
    grantPro30: 'Grant Pro 30 days',
    removeGrace: 'Remove grace',
    removeOverride: 'Remove override',
    setTrial: 'Set trial end',
    extendStripeTrial: 'Extend Stripe trial',
    extendStripeTrialPrompt: 'Stripe trial days',
    extendStripeTrialToast: 'Stripe trial updated',
    extendStripeTrialDbOnlyToast:
      'Trial saved in our database only. Stripe subscription is still incomplete: fix or cancel the pending checkout in Stripe.',
    endStripeTrial: 'End Stripe trial',
    quickEndTrial: 'End trial',
    quickEndTrialTitle: 'End trial now (same action as below)',
    endStripeTrialConfirm: 'End Stripe trial now?',
    endStripeTrialToast: 'Stripe trial ended',
    stripeTrialBlocked: 'Stripe cannot modify the trial in this status',
    stripeStatus: 'Stripe status',
    viewDetails: 'View details',
    cancelSubscription: 'Cancel subscription',
    pauseSubscription: 'Pause subscription',
    resumeSubscription: 'Resume subscription',
    openStripe: 'Open Stripe customer',
    configureStripe: 'Configure Stripe',
    stripeCustomerId: 'Stripe Customer ID',
    stripeSubscriptionId: 'Stripe Subscription ID',
    stripeIdsRequired: 'Enter Customer or Subscription',
    stripeLinked: 'Stripe updated',
    forceSync: 'Force Stripe sync',
    sectionManage: 'Manage',
    sectionSubscription: 'Subscription',
    sectionPlan: 'Plan',
    sectionSystem: 'System',
    sectionDanger: 'Danger',
    sectionPrimary: 'Primary actions',
    sectionSubscriptionMgmt: 'Subscription management',
    sectionTrialGrace: 'Trial / grace controls',
    sectionDangerZone: 'Danger zone'
  },
  billingOverride: {
    sectionTitle: 'Manual billing',
    activate: 'Activate access',
    deactivate: 'Deactivate access',
    extend: 'Extend access',
    trial: 'Manual trial',
    clear: 'Clear override',
    label: 'Billing',
    finalStatus: 'Final billing status',
    promptDays: 'Access days',
    promptReason: 'Reason (optional)',
    invalidDays: 'Invalid days',
    confirmClear: 'Clear manual override?',
    toastActivated: 'Access activated',
    toastDeactivated: 'Access deactivated',
    toastExtended: 'Access extended',
    toastTrial: 'Manual trial applied',
    toastCleared: 'Override cleared',
    until: 'until {date}',
    status: {
      active: 'active',
      inactive: 'inactive',
      trial: 'trial'
    },
    source: {
      manual: 'manual',
      stripe: 'stripe',
      trial: 'trial',
      system: 'system'
    }
  },
  orgTrialModal: {
    title: 'Set trial end',
    labelDate: 'Trial end (local time)',
    placeholderDate: 'Pick date and time',
    labelReason: 'Reason (required)',
    placeholderReason: 'Describe why you are changing the trial',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    errorRequired: 'Date and reason are required'
  },
  orgDeleteModal: {
    title: 'Delete organization',
    warningTitle: '⚠️ This action is irreversible.',
    warningIntro: 'This will delete:',
    itemOrg: 'The organization',
    itemUsers: 'All users',
    itemTime: 'All time entries',
    itemData: 'All associated data',
    confirmTitle: 'Confirmation',
    orgNameHint: 'Organization name: {name}',
    placeholder: 'Type the organization name to confirm',
    acknowledge: 'I understand this action cannot be undone',
    confirmDelete: 'Delete permanently'
  },
  org: {
    title: 'Organizations',
    subtitle: 'Multi-tenant: list of all organizations',
    searchPlaceholder: 'Search by name or ID…',
    empty: 'No organizations',
    createNew: 'Create organization',
    planChangeTitle: 'Change plan',
    planRequired: 'Select a plan',
    nameLabel: 'Name',
    adminName: 'Admin name',
    adminNamePlaceholder: 'Admin person name',
    adminEmail: 'Admin email',
    adminPassword: 'Initial password',
    adminPasswordConfirm: 'Confirm password',
    errorEmailRequired: 'Email is required',
    errorPasswordRequired: 'Password must be at least 6 characters',
    errorPasswordMatch: 'Passwords do not match',
    name: 'Name',
    namePlaceholder: 'Organization name',
    plan: 'Plan',
    status: 'Status',
    users: 'Users',
    createdAt: 'Created',
    created: 'Organization created',
    statusActive: 'Active',
    statusPaused: 'Paused',
    statusSuspended: 'Suspended',
    create: 'Create organization',
    edit: 'Edit organization',
    activate: 'Activate',
    deactivate: 'Deactivate',
    delete: 'Delete organization',
    confirmDelete: 'This will delete the organization and all its users.',
    deleted: 'Organization deleted',
    confirmCancelSub: 'Cancel this organization’s subscription?',
    confirmPauseSub: 'Pause billing for this organization?',
    confirmResumeSub: 'Resume billing for this organization?',
    toastCancelled: 'Subscription cancelled',
    toastPaused: 'Subscription paused',
    toastResumed: 'Subscription resumed',
    toastSynced: 'Billing synchronized',
    confirmDeactivate: 'Deactivate this organization? It will be suspended.',
    confirmActivate: 'Activate this organization?',
    planPlaceholder: 'e.g. enterprise',
    planStarter: 'Starter',
    planGrowth: 'Growth',
    planEnterprise: 'Enterprise',
    planBusiness: 'Business',
    planLabelMap: {
      starter: 'Starter',
      growth: 'Growth',
      business: 'Business',
      enterprise: 'Enterprise',
      basic: 'Basic'
    },
    badgeTrial: 'TRIAL',
    usersLabel: 'users',
    planUsersLabel: 'Users',
    planUsersLimit: 'up to {count} {label}',
    planUsersPlaceholder: 'e.g. 20',
    planTrialDaysLabel: 'Trial days (optional)',
    planTrialDaysPlaceholder: 'e.g. 14',
    planReasonLabel: 'Reason (required)',
    planReasonPlaceholder: 'Describe why you are changing the plan',
    accessUntil: 'access until {date}',
    slug: 'Slug / Org code',
    slugPlaceholder: 'org-code',
    slugHelp: 'Used in URLs and internal references.',
    timezone: 'Time zone',
    language: 'Default language',
    languageEn: 'English',
    languageEs: 'Spanish',
    contactEmail: 'Contact email',
    contactEmailPlaceholder: 'admin@company.com',
    billingEmail: 'Billing email',
    billingEmailPlaceholder: 'billing@company.com',
    country: 'Country',
    countryPlaceholder: 'United States',
    phone: 'Phone',
    phonePlaceholder: '+1 555 000 0000',
    maxUsers: 'Max users',
    maxUsersPlaceholder: 'e.g. 250',
    notes: 'Notes',
    notesPlaceholder: 'Internal notes',
    sectionDetails: 'Organization details',
    sectionContact: 'Contact & billing',
    sectionLimits: 'Limits & settings',
    optional: 'Optional',
    optionalTooltip: 'Optional field',
    errorNameRequired: 'Name is required.',
    errorNameMin: 'Name must be at least 2 characters.',
    errorSlugRequired: 'Slug is required.',
    errorSlugMin: 'Slug must be at least 2 characters.',
    errorSlugInvalid: 'Slug can only include lowercase letters, numbers, and hyphens.',
    errorContactEmailRequired: 'Contact email is required.',
    errorContactEmailInvalid: 'Enter a valid email.',
    errorBillingEmailInvalid: 'Enter a valid billing email.',
    errorTimezoneRequired: 'Time zone is required.',
    errorLanguageRequired: 'Language is required.',
    errorFixForm: 'Please fix the highlighted fields.',
    orgId: 'Org ID'
  },
  users: {
    adminEmail: 'Admin email',
    title: 'Users',
    subtitle: 'All platform users',
    searchPlaceholder: 'Search by name or email…',
    empty: 'No users',
    name: 'Name',
    email: 'Email',
    organization: 'Organization',
    role: 'Role',
    createdAt: 'Created',
    create: 'Create user',
    edit: 'Edit',
    delete: 'Delete',
    deleting: 'Deleting…',
    deleteImpact: 'Active sessions will be closed and the user will not be able to sign in. This cannot be undone in the UI.',
    confirmDeleteStrong: 'Type the user email to enable Delete:',
    deactivate: 'Deactivate',
    activate: 'Activate',
    resetPassword: 'Reset password',
    moveOrg: 'Move organization',
    changeRole: 'Change role',
    active: 'Active',
    inactive: 'Inactive',
    newPassword: 'New password',
    newPasswordPlaceholder: 'At least 6 characters',
    confirmPassword: 'Confirm password',
    confirmPasswordPlaceholder: 'Re-enter password',
    passwordMismatch: 'Passwords do not match',
    filterAllRoles: 'All roles',
    filterAllStatus: 'All statuses',
    sortNewest: 'Newest',
    sortOldest: 'Oldest',
    sortNameAsc: 'Name A-Z',
    sortNameDesc: 'Name Z-A',
    sortRole: 'Role',
    sortOrg: 'Organization',
    emptyFiltered: 'No users match the filters.',
    filterOrgAll: 'Organization (All)',
    filterRolesAll: 'All roles',
    filterStatesAll: 'All statuses',
    selectOrg: 'Select organization',
    roleEmployee: 'Employee',
    roleAdmin: 'Admin',
    roleSupervisor: 'Supervisor',
    roleSuperadmin: 'Superadmin',
    roleOwner: 'Owner',
    confirmDelete: 'Delete this user? This action cannot be undone.',
    confirmDeactivate: 'Deactivate this user? They will lose access until reactivated.',
    confirmActivate: 'Activate this user and restore access?',
    userId: 'User ID',
    orgId: 'Org ID'
  },
  timeEntries: {
    title: 'Time entries',
    subtitle: 'Entries from all organizations',
    empty: 'No time entries',
    from: 'From',
    to: 'To',
    user: 'User',
    organization: 'Organization',
    clockIn: 'Clock in',
    clockOut: 'Clock out',
    hours: 'Hours',
    status: 'Status',
    statusCompleted: 'Completed',
    statusActive: 'In progress',
    statusError: 'Error',
    filterByOrg: 'Organization',
    filterByUser: 'User',
    exportCsv: 'Export CSV'
  },
  audit: {
    title: 'Audit',
    subtitle: 'Backoffice action log (who, what, when, IP)',
    empty: 'No audit records',
    events: {
      dashboard_view: 'Dashboard viewed',
      preferences_update: 'Preferences updated',
      superadmin_login_success: 'Superadmin logged in',
      organization_created: 'Organization created',
      organization_updated: 'Organization updated',
      organization_deleted: 'Organization deleted',
      organization_activated: 'Organization activated',
      organization_deactivated: 'Organization deactivated',
      user_created: 'User created',
      user_updated: 'User updated',
      user_deleted: 'User deleted',
      password_changed: 'Password changed',
      password_reset_requested: 'Password reset requested',
      twofa_enabled: 'Two-factor enabled',
      twofa_disabled: 'Two-factor disabled'
    },
    actorFilter: 'Actor',
    actorFilterPlaceholder: 'Search by actor…',
    actionFilter: 'Action',
    actionFilterPlaceholder: 'Filter by action…',
    clearFilters: 'Clear filters',
    exportCsv: 'Export CSV',
    date: 'Date',
    actor: 'Actor',
    action: 'Action',
    resource: 'Resource',
    ip: 'IP',
    result: 'Result',
    resultOk: 'ok',
    resultError: 'failed',
    resultWarning: 'warning',
    metadata: 'Detail',
    before: 'Before',
    after: 'After'
  },
  notifDiagnostics: {
    title: 'Notification Diagnostics',
    subtitle: 'Backend/app notification events of all types; automatic correlation is most reliable for reminders (break end, pending clock in/out) — other types may be listed separately',
    empty: 'No notification diagnostic records',
    userIdFilterPlaceholder: 'Filter by userId…',
    dedupeKeyFilterPlaceholder: 'Filter by dedupeKey…',
    notifTypeFilterPlaceholder: 'Type',
    notifTypeAll: 'All types',
    search: 'Search',
    user: 'User',
    backendEvents: 'Backend',
    clientEvents: 'App (client)',
    noBackendEvents: 'No backend events',
    noClientEvents: 'No client events',
    failed: 'FAILED',
    type: 'type',
    unknownDate: 'Unknown date',
    noDedupeKey: 'No grouping key (not filterable by dedupeKey)'
  },
  systemStatus: {
    title: 'System Status',
    subtitle: 'Recent backend and app errors, plus general health — refreshes itself every 5 seconds',
    healthServer: 'Server',
    healthDb: 'Database',
    healthUptime: 'Up for',
    statusOk: 'OK',
    statusError: 'ERROR',
    empty: 'No recent errors',
    sourceFilterLabel: 'Source',
    severityFilterLabel: 'Severity',
    filterAll: 'All',
    sourceBackend: 'Backend',
    sourceClient: 'App (client)',
    severityError: 'Error',
    severityWarning: 'Warning',
    backendBadge: 'Backend',
    clientBadge: 'App',
    userLabel: 'User'
  },
  search: {
    placeholder: 'Search organization, user, project…',
    sections: {
      organizations: 'Organizations',
      users: 'Users',
      projects: 'Projects'
    },
    noResults: 'No results'
  },
  toast: {
    saved: 'Saved',
    error: 'Something went wrong',
    created: 'Created',
    deleted: 'Deleted',
    updated: 'Updated',
    passwordReset: 'Password updated'
  },
  twoFa: {
    title: 'Two-factor authentication',
    subtitle: 'Use an authenticator app (Google Authenticator, Authy) or recovery codes.',
    status: 'Status',
    enabled: '2FA is enabled.',
    disabled: '2FA is not enabled.',
    enable: 'Enable 2FA',
    disable: 'Disable 2FA',
    scanQr: 'Scan this QR code with your app, or enter the code manually.',
    manualCode: 'Manual code',
    enterCode: 'Enter the 6-digit code from your app',
    confirm: 'Confirm',
    recoveryCodes: 'Recovery codes (save these; they will not be shown again)',
    recoveryCodesDesc: 'Each code can be used once if you lose access to your app.',
    disablePassword: 'Your password',
    disableCode: 'Current 2FA code or recovery code',
    disableConfirm: 'Disable 2FA'
  },
  security: {
    overviewTitle: 'Security overview',
    accountEmail: 'Account email',
    twoFactor: 'Two-factor authentication',
    enabled: 'Enabled',
    disabled: 'Disabled',
    lastLogin: 'Last login',
    lastLoginIp: 'Last login IP',
    authApp: 'Authenticator app',
    recoveryCodes: 'Recovery codes',
    available: 'Available',
    notGenerated: 'Not generated',
    recoveryTitle: 'Recovery codes',
    recoveryDesc: 'Recovery codes can be used if you lose access to your authenticator.',
    viewRecovery: 'View recovery codes',
    generateRecovery: 'Generate recovery codes',
    dangerTitle: 'Danger zone',
    dangerDesc: 'Disabling 2FA will reduce the security of this account.',
    disableModalTitle: 'Disable two-factor authentication?',
    disableModalText: 'This will reduce account security.',
    disableConfirm: 'Disable'
  },
  backups: {
    title: 'Database Backups',
    subtitle: 'Manage and download production database backups.',
    listTitle: 'Available backups',
    name: 'Filename',
    size: 'Size',
    date: 'Created at',
    actions: 'Actions',
    download: 'Download',
    createBtn: 'Create manual backup',
    creating: 'Creating backup...',
    noBackups: 'No backups found.',
    successCreated: 'Backup created successfully',
    errorLoad: 'Error loading backups',
    errorCreate: 'Error creating backup',
    backupsDir: 'Directory on server'
  }
};
